/**
 * ThreeG TMS Utility Functions
 *
 */
import { ServiceOption } from 'lib/api/getServiceOptions';
import { TransportType } from 'lib/api/getTransportTypes';
import { Commodity, CostLineItem, TMSCustomer } from 'types/Load';
import { MaybeUndef, Undef } from 'types/UtilityTypes';
import { LoadPayloadWithOptionalConsigneeMustDeliver } from 'types/api/LoadPayload';

type LoadWithOptionalDeliveryDate = {
  mode?: string;
  consignee: { mustDeliver?: unknown };
};

type ThreeGLoadPayload<T extends LoadWithOptionalDeliveryDate> =
  LoadPayloadWithOptionalConsigneeMustDeliver<T>;

/**
 * Dock hours open/close: ensure HH:MM string for 3G create-load payload.
 * Form may have string (e.g. from suggestion) or Date (from denormalize).
 * ThreeG is timezone-aware (isLoadTimezoneAgnostic: false); Date inputs are created
 * via new Date(value) without UTC normalization, so local time components are used.
 */
export function openCloseToHHMM(v: unknown): string {
  if (typeof v === 'string' && /^\d{1,2}:\d{2}/.test(v)) {
    const [h, m] = v.split(':');
    return `${(h ?? '').padStart(2, '0')}:${(m ?? '').padStart(2, '0')}`;
  }
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${String(v.getHours()).padStart(2, '0')}:${String(v.getMinutes()).padStart(2, '0')}`;
  }
  return '';
}

function isEmptyDeliveryDate(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (value instanceof Date) {
    return isNaN(value.getTime());
  }
  return false;
}

export function omitEmptyThreeGLTLDeliveryDate<
  T extends LoadWithOptionalDeliveryDate,
>(load: T): ThreeGLoadPayload<T> {
  if (load.mode !== 'LTL') {
    return load as ThreeGLoadPayload<T>;
  }

  if (!isEmptyDeliveryDate(load.consignee.mustDeliver)) {
    return load as ThreeGLoadPayload<T>;
  }

  const { mustDeliver: _mustDeliver, ...consigneeWithoutDeliveryDate } =
    load.consignee as { mustDeliver?: unknown } & Record<string, unknown>;

  return {
    ...load,
    consignee: consigneeWithoutDeliveryDate,
  } as ThreeGLoadPayload<T>;
}

/**
 * Normalizes a transport type string to match dropdown expectations.
 * Handles case-insensitive matching by value or label. Returns '' when the
 * value is empty or matches no option, so the rep picks the equipment instead
 * of getting the first option.
 */
export function normalizeTransportType(
  transportType: Undef<string>,
  transportTypes: MaybeUndef<TransportType[]>
): string {
  if (!transportType || !transportTypes || transportTypes.length === 0) {
    return '';
  }

  const normalizedTransportType = transportType.toLowerCase();

  const valueMatch = transportTypes.find(
    (type) => type.value?.toLowerCase() === normalizedTransportType
  );
  if (valueMatch) {
    return valueMatch.value || valueMatch.label || '';
  }

  const labelMatch = transportTypes.find(
    (type) => type.label?.toLowerCase() === normalizedTransportType
  );
  if (labelMatch) {
    return labelMatch.value || labelMatch.label || '';
  }

  if (
    normalizedTransportType.includes('reefer') ||
    normalizedTransportType.includes('refrigerat')
  ) {
    const preferred = getPreferredRefrigeratedTransportType(transportTypes);
    if (preferred) return preferred;
  }

  return '';
}

export function getPreferredRefrigeratedTransportType(
  transportTypes: MaybeUndef<TransportType[]>
): string {
  if (!transportTypes || transportTypes.length === 0) return '';
  const isRefrigerated = (label: string, value: string) =>
    label.includes('reefer') ||
    value.includes('reefer') ||
    label.includes('refrigerat') ||
    value.includes('refrigerat');
  const reefer53 = transportTypes.find((type) => {
    const label = type.label?.toLowerCase() || '';
    const value = type.value?.toLowerCase() || '';
    return (
      (label.includes('53') || value.includes('53')) &&
      isRefrigerated(label, value)
    );
  });
  if (reefer53) return reefer53.value || reefer53.label || '';
  const anyReefer = transportTypes.find((type) => {
    const label = type.label?.toLowerCase() || '';
    const value = type.value?.toLowerCase() || '';
    return isRefrigerated(label, value);
  });
  return anyReefer ? anyReefer.value || anyReefer.label || '' : '';
}

/**
 * Normalizes a service type string to match dropdown expectations.
 * Handles case-insensitive matching by ID or name, with fallback to first available.
 */
export function normalizeServiceType(
  serviceType: Undef<string>,
  serviceOptions: MaybeUndef<ServiceOption[]>
): string {
  if (!serviceType || !serviceOptions || serviceOptions.length === 0) {
    return serviceOptions && serviceOptions.length > 0
      ? serviceOptions[0].id || serviceOptions[0].name || ''
      : '';
  }

  const normalizedServiceType = serviceType.toLowerCase().trim();

  const idMatch = serviceOptions.find(
    (option) => option.id?.toLowerCase().trim() === normalizedServiceType
  );
  if (idMatch) {
    return String(idMatch.id || idMatch.name || '');
  }

  const normalizeString = (str: string) =>
    str.toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizedInput = normalizeString(serviceType);

  const nameMatch = serviceOptions.find((option) => {
    if (!option.name) return false;
    return normalizeString(option.name) === normalizedInput;
  });

  if (nameMatch) {
    return String(nameMatch.id || nameMatch.name || '');
  }

  return serviceOptions.length > 0
    ? String(serviceOptions[0].id || serviceOptions[0].name || '')
    : '';
}

/**
 * Normalizes transport type by combining transportType and transportSize to match
 * full equipment type names (e.g., "VAN" + "53 ft" → "Dedicated 53 Dry Van").
 * Falls back to basic transportType matching if size-based matching fails.
 */
export function normalizeTransportTypeWithSize(
  transportType: Undef<string>,
  transportSize: Undef<string>,
  transportTypes: MaybeUndef<TransportType[]>
): string {
  // First try basic transportType matching (handles cases without size)
  const basicMatch = normalizeTransportType(transportType, transportTypes);

  // If no transportSize provided, return basic match
  if (!transportSize || !transportTypes || transportTypes.length === 0) {
    return basicMatch;
  }

  // Normalize inputs for matching
  const normalizedType = transportType?.toLowerCase().trim() || '';
  const normalizedSize = transportSize.toLowerCase().trim();

  // Extract numeric size (e.g., "53 ft" → "53", "48'" → "48")
  const sizeMatch = normalizedSize.match(/(\d+)/);
  const sizeNumber = sizeMatch ? sizeMatch[1] : null;

  if (!sizeNumber) {
    // No numeric size found, return basic match
    return basicMatch;
  }

  // Build search patterns to match equipment type names
  // Patterns to try (in order of specificity):
  // 1. "53" + "van" → "Dedicated 53 Dry Van", "Dedicated 53' Dry Van"
  // 2. "53" + "reefer" → "Dedicated 53' Reefer"
  // 3. "53" + "flatbed" → "Dedicated 53' Flatbed"
  // 4. Just "53" → any equipment with "53"

  const searchPatterns: string[] = [];

  // Add patterns with size + type
  if (normalizedType) {
    // Map common transport type names to equipment type keywords
    const typeKeywords: string[] = [];
    if (normalizedType.includes('van')) {
      typeKeywords.push('dry van', 'dryvan', 'van');
    }
    if (
      normalizedType.includes('reefer') ||
      normalizedType.includes('refrigerat')
    ) {
      typeKeywords.push('reefer', 'refrigerat');
    }
    if (normalizedType.includes('flatbed') || normalizedType.includes('flat')) {
      typeKeywords.push('flatbed', 'flat');
    }

    // Build patterns: "53" + each keyword
    typeKeywords.forEach((keyword) => {
      searchPatterns.push(`${sizeNumber}.*${keyword}`);
      searchPatterns.push(`${keyword}.*${sizeNumber}`);
      searchPatterns.push(`dedicated.*${sizeNumber}.*${keyword}`);
    });
  }

  // Add pattern with just size (less specific)
  searchPatterns.push(sizeNumber);

  // Try to find a match using the patterns
  for (const pattern of searchPatterns) {
    const regex = new RegExp(pattern, 'i');
    const match = transportTypes.find((type) => {
      const label = type.label?.toLowerCase() || '';
      const value = type.value?.toLowerCase() || '';

      // Skip "All Equipment Types" - we want specific equipment types
      if (
        label.includes('all equipment') ||
        value.includes('all equipment') ||
        label === 'all equipment types' ||
        value === 'all equipment types'
      ) {
        return false;
      }

      return regex.test(label) || regex.test(value);
    });

    if (match) {
      return match.value || match.label || '';
    }
  }

  // No size-based match found, return basic transportType match
  // But skip "All Equipment Types" if it's the first option
  if (basicMatch) {
    const basicMatchLower = basicMatch.toLowerCase();
    if (
      !basicMatchLower.includes('all equipment') &&
      basicMatchLower !== 'all equipment types'
    ) {
      return basicMatch;
    }
  }

  // If basic match is "All Equipment Types", try to find any other match
  // that contains the transport type
  if (normalizedType) {
    const typeMatch = transportTypes.find((type) => {
      const label = type.label?.toLowerCase() || '';
      const value = type.value?.toLowerCase() || '';

      // Skip "All Equipment Types"
      if (label.includes('all equipment') || value.includes('all equipment')) {
        return false;
      }

      // Try to match the transport type keyword
      if (normalizedType.includes('van')) {
        return label.includes('van') || value.includes('van');
      }
      if (
        normalizedType.includes('reefer') ||
        normalizedType.includes('refrigerat')
      ) {
        return (
          label.includes('reefer') ||
          value.includes('reefer') ||
          label.includes('refrigerat') ||
          value.includes('refrigerat')
        );
      }
      if (
        normalizedType.includes('flatbed') ||
        normalizedType.includes('flat')
      ) {
        return (
          label.includes('flatbed') ||
          value.includes('flatbed') ||
          label.includes('flat') ||
          value.includes('flat')
        );
      }

      return false;
    });

    if (typeMatch) {
      return typeMatch.value || typeMatch.label || '';
    }
  }

  // Last resort: return first option that's not "All Equipment Types"
  const firstNonAllOption = transportTypes.find((type) => {
    const label = type.label?.toLowerCase() || '';
    const value = type.value?.toLowerCase() || '';
    return (
      !label.includes('all equipment') &&
      !value.includes('all equipment') &&
      label !== 'all equipment types' &&
      value !== 'all equipment types'
    );
  });

  if (firstNonAllOption) {
    return firstNonAllOption.value || firstNonAllOption.label || '';
  }

  // Final fallback: return basic match even if it's "All Equipment Types"
  return basicMatch;
}

/**
 * Creates a flat-rate cost line item for rate data.
 * Used when converting legacy charge fields (like customerLineHaulCharge) to line items.
 */
export function createFlatRateCostLineItem(
  label: string,
  amount: number
): CostLineItem {
  return {
    label,
    unitBasis: 'Flat',
    quantity: 1,
    ratePerUnitUSD: amount,
    totalChargeUSD: amount,
    note: '',
  };
}

export function hasCustomerDisplayName(
  customer: MaybeUndef<Pick<TMSCustomer, 'name'>>
): boolean {
  return customer?.name != null && String(customer.name).trim() !== '';
}

export function mergeResolvedCustomerIntoList(
  resolvedCustomer: TMSCustomer,
  customers: MaybeUndef<TMSCustomer[]>
): TMSCustomer[] {
  const resolvedExternalId = String(
    resolvedCustomer.externalTMSID ?? ''
  ).trim();
  const customerList = customers ?? [];

  if (!resolvedExternalId) {
    return customerList;
  }

  const existingIndex = customerList.findIndex(
    (customer) =>
      customer.externalTMSID != null &&
      String(customer.externalTMSID).trim() === resolvedExternalId
  );

  if (existingIndex === -1) {
    return [resolvedCustomer, ...customerList];
  }

  if (hasCustomerDisplayName(customerList[existingIndex])) {
    return customerList;
  }

  const nextCustomers = [...customerList];
  nextCustomers[existingIndex] = resolvedCustomer;
  return nextCustomers;
}

function safeNumber(value: number | undefined): number {
  if (value === undefined || Number.isNaN(value)) return 0;
  return value;
}

function positiveNumber(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return parsed;
}

/** Extraction/Turvo fields that must not shadow ThreeG Builder edits on create. */
function omitThreeGExtractionOnlyCommodityFields(
  commodity: Commodity
): Commodity {
  const {
    totalPieces: _totalPieces,
    grossWeight: _grossWeight,
    netWeight: _netWeight,
    ...rest
  } = commodity;

  return rest;
}

/**
 * Collapse LLM/extraction commodity fields into ThreeG Builder form fields and
 * drop shadow fields so RHF only tracks values the sidebar edits.
 */
export function normalizeThreeGCommodityFromSuggestion(
  commodity: Commodity
): Commodity {
  const quantity =
    positiveNumber(commodity.quantity) ||
    positiveNumber(commodity.totalPieces?.val);

  const weightTotal =
    positiveNumber(commodity.weightTotal) ||
    positiveNumber(commodity.grossWeight?.val) ||
    positiveNumber(commodity.netWeight?.val);

  return omitThreeGExtractionOnlyCommodityFields({
    ...commodity,
    quantity,
    weightTotal,
  });
}

/**
 * Strip extraction-only commodity fields before create-load so backend uses
 * Builder-edited quantity/handlingQuantity instead of stale totalPieces.
 */
export function normalizeThreeGCommodityForCreate(
  commodity: Commodity
): Commodity {
  return omitThreeGExtractionOnlyCommodityFields({
    ...commodity,
    weightTotal: Number(commodity.weightTotal) || 0,
    handlingQuantity: Number(commodity.handlingQuantity) || 0,
    quantity: Number(commodity.quantity) || 0,
    length: Number(commodity.length) || 0,
    width: Number(commodity.width) || 0,
    height: Number(commodity.height) || 0,
  });
}

/**
 * Returns true if the commodity has any count data (HU count or piece count).
 * Used to decide when to derive specs from items vs use suggested counts.
 */
export function hasCommodityCountData(commodity: Commodity): boolean {
  const hu = safeNumber(commodity.handlingQuantity);
  const pieces = safeNumber(commodity.quantity);
  return hu > 0 || pieces > 0;
}

/**
 * Computes total pallets (HU count) and total pieces from 3G commodities.
 * 3G uses handlingQuantity as HU/pallet count and quantity as piece count per line.
 */
export function calculatePalletsAndPiecesFromCommodities(
  commodities: Commodity[]
): { totalPallets: number; totalPieces: number } {
  const totalPallets = commodities.reduce(
    (sum, c) => sum + safeNumber(c.handlingQuantity),
    0
  );
  const totalPieces = commodities.reduce(
    (sum, c) => sum + safeNumber(c.quantity),
    0
  );
  return { totalPallets, totalPieces };
}

/** Format timezone for display only. Does not change stored/API data. 3G sends mixed camelCase and IANA-style IDs. */
export function formatTimezoneForDisplay(tz: string | undefined): string {
  if (!tz?.trim()) return '';
  const trimmed = tz.trim();
  // Labels for 3G production timezones (camelCase and IANA). Add new ones here as they appear.
  const friendly: Record<string, string> = {
    AmericaAnchorage: 'Alaska',
    AmericaAtikokan: 'Eastern (Atikokan)',
    AmericaBoise: 'Mountain (Boise)',
    'America/Boise': 'Mountain (Boise)',
    AmericaChicago: 'Central',
    'America/Chicago': 'Central',
    AmericaChihuahua: 'Chihuahua',
    AmericaDawsonCreek: 'Pacific (Dawson Creek)',
    AmericaDenver: 'Mountain',
    'America/Denver': 'Mountain',
    'America/Detroit': 'Eastern (Detroit)',
    AmericaEdmonton: 'Mountain (Edmonton)',
    'America/Edmonton': 'Mountain (Edmonton)',
    AmericaGooseBay: 'Atlantic (Goose Bay)',
    AmericaHalifax: 'Atlantic',
    AmericaHermosillo: 'Mountain (Hermosillo)',
    AmericaIndianaIndianapolis: 'Eastern (Indiana)',
    AmericaIndianaKnox: 'Central (Indiana)',
    AmericaIndianaTellCity: 'Central (Indiana)',
    AmericaJuneau: 'Alaska (Juneau)',
    AmericaLosAngeles: 'Pacific',
    'America/Los_Angeles': 'Pacific',
    AmericaMenominee: 'Central (Menominee)',
    AmericaMexicoCity: 'Mexico City',
    AmericaMoncton: 'Atlantic (Moncton)',
    AmericaMonterrey: 'Central (Monterrey)',
    AmericaMontreal: 'Eastern (Montreal)',
    AmericaNewYork: 'Eastern',
    'America/New_York': 'Eastern',
    AmericaPhoenix: 'Arizona',
    AmericaPuertoRico: 'Atlantic (Puerto Rico)',
    AmericaRainyRiver: 'Central (Rainy River)',
    AmericaRegina: 'Central (Regina)',
    AmericaStJohns: 'Newfoundland',
    AmericaThunderBay: 'Eastern (Thunder Bay)',
    AmericaTijuana: 'Pacific (Tijuana)',
    AmericaToronto: 'Eastern (Toronto)',
    'America/Toronto': 'Eastern (Toronto)',
    AmericaVancouver: 'Pacific (Vancouver)',
    'America/Vancouver': 'Pacific (Vancouver)',
    AmericaWhitehorse: 'Yukon',
    AmericaWinnipeg: 'Central (Winnipeg)',
    'America/Winnipeg': 'Central (Winnipeg)',
    AmericaYellowknife: 'Mountain (Yellowknife)',
    CanadaAtlantic: 'Atlantic',
    CanadaCentral: 'Central',
    CanadaCentralNoDst: 'Central (no DST)',
    CanadaEastern: 'Eastern',
    CanadaMountain: 'Mountain',
    CanadaNewfoundland: 'Newfoundland',
    CanadaPacific: 'Pacific',
    MexicoCentral: 'Mexico Central',
    MexicoMountain: 'Mexico Mountain',
    PacificHonolulu: 'Hawaii',
    USAlaska: 'Alaska',
    USArizona: 'Arizona',
    USCentral: 'Central',
    USEastern: 'Eastern',
    USHawaii: 'Hawaii',
    USMountain: 'Mountain',
    USPacific: 'Pacific',
  };
  if (friendly[trimmed]) return friendly[trimmed];
  // Fallback: insert space before capitals so "AmericaNewYork" → "America New York"
  return trimmed.replace(/([a-z])([A-Z])/g, '$1 $2');
}
